/**
 * Settings storage (web-client.md §4). localStorage holds ONLY disposable prefs: settings, language,
 * last server URL, effective-flags cache. The access token is NEVER stored (security §2.1).
 *
 * core/ is DOM-free, so the browser's `localStorage` is injected as a {@link KeyValueStore}; the app
 * layer supplies the real one. A {@link MemoryKeyValueStore} backs tests and SSR/no-storage cases.
 */
export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export class MemoryKeyValueStore implements KeyValueStore {
  private readonly map = new Map<string, string>();
  get(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  set(key: string, value: string): void {
    this.map.set(key, value);
  }
  remove(key: string): void {
    this.map.delete(key);
  }
}

const KEYS = {
  serverUrl: 'ts.serverUrl',
  language: 'ts.language',
  theme: 'ts.theme',
  flags: 'ts.flags',
} as const;

export type ThemePref = 'auto' | 'dark' | 'light' | 'mono';

export class SettingsStore {
  constructor(private readonly kv: KeyValueStore) {}

  getServerUrl(fallback: string): string {
    return this.kv.get(KEYS.serverUrl) ?? fallback;
  }
  setServerUrl(url: string): void {
    this.kv.set(KEYS.serverUrl, url);
  }

  getLanguage(fallback: string): string {
    return this.kv.get(KEYS.language) ?? fallback;
  }
  setLanguage(lang: string): void {
    this.kv.set(KEYS.language, lang);
  }

  getTheme(): ThemePref {
    const v = this.kv.get(KEYS.theme);
    return v === 'dark' || v === 'light' || v === 'mono' ? v : 'auto';
  }
  setTheme(theme: ThemePref): void {
    this.kv.set(KEYS.theme, theme);
  }

  getFlags(): Record<string, boolean> {
    const raw = this.kv.get(KEYS.flags);
    if (!raw) return {};
    try {
      return JSON.parse(raw) as Record<string, boolean>;
    } catch {
      return {};
    }
  }
  setFlags(flags: Record<string, boolean>): void {
    this.kv.set(KEYS.flags, JSON.stringify(flags));
  }
}
